<template>
  <HrPageLayout
    title="Company Configuration"
    subtitle="Manage your organisation details and branding for payslips and reports"
    icon="🏢"
    :breadcrumb-items="[
      { label: 'System Administration' },
      { label: 'Company Configuration' }
    ]"
  >
    <template #header-actions>
      <span v-if="isDirty && !loading" class="cc-pill cc-pill--warn">
        <i class="bx bx-edit-alt"></i> Unsaved changes
      </span>
      <span v-else-if="!loading && !loadError" class="cc-pill cc-pill--ok">
        <i class="bx bx-check-circle"></i> All changes saved
      </span>
      <button
        type="button"
        class="cc-btn cc-btn--ghost"
        :disabled="!isDirty || saving || loading"
        @click="discard"
      >
        <i class="bx bx-undo"></i> Discard
      </button>
      <button
        type="submit"
        form="company-config-form"
        class="cc-btn cc-btn--primary"
        :disabled="saving || loading || !isDirty"
      >
        <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
        {{ saving ? 'Saving...' : 'Save changes' }}
      </button>
    </template>

    <!-- Loading -->
    <div v-if="loading" class="cc-skeleton-wrap" aria-busy="true">
      <div class="cc-card cc-skeleton-card">
        <div class="cc-sk cc-sk-logo"></div>
        <div class="cc-sk-lines">
          <div class="cc-sk cc-sk-line cc-sk-line--lg"></div>
          <div class="cc-sk cc-sk-line"></div>
          <div class="cc-sk cc-sk-line cc-sk-line--sm"></div>
        </div>
      </div>
      <div class="cc-card cc-skeleton-card cc-skeleton-card--form">
        <div class="cc-sk cc-sk-field" v-for="n in 6" :key="n"></div>
      </div>
    </div>

    <!-- Load failure -->
    <div v-else-if="loadError" class="cc-card cc-empty">
      <span class="cc-empty-icon"><i class="bx bx-cloud-download"></i></span>
      <h2 class="cc-empty-title">Could not load company configuration</h2>
      <p class="cc-empty-text">Check your connection and try again — nothing has been changed.</p>
      <button type="button" class="cc-btn cc-btn--primary" @click="load">
        <i class="bx bx-refresh"></i> Try again
      </button>
    </div>

    <form v-else id="company-config-form" class="cc-wrap" novalidate @submit.prevent="save">
      <!-- Brand identity: logo + live document header -->
      <section class="cc-card cc-brand">
        <div class="cc-brand-grid">
          <div class="cc-brand-col">
            <div class="cc-col-head">
              <h2 class="cc-col-title"><i class="bx bx-image-alt"></i> Company logo</h2>
              <span class="cc-col-note">PNG, JPG, GIF or WebP · max 2MB</span>
            </div>

            <div
              class="cc-drop"
              :class="{ 'cc-drop--filled': !!logoPreviewUrl, 'cc-drop--active': dragOver }"
              role="button"
              tabindex="0"
              :aria-label="logoPreviewUrl ? 'Replace company logo' : 'Upload company logo'"
              @click="openFilePicker"
              @keydown.enter.prevent="openFilePicker"
              @keydown.space.prevent="openFilePicker"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onLogoDrop"
            >
              <img
                v-if="logoPreviewUrl"
                :src="logoPreviewUrl"
                alt="Company logo"
                class="cc-drop-img"
                @error="onLogoError"
              />
              <div v-else class="cc-drop-empty">
                <i class="bx bx-cloud-upload"></i>
                <span class="cc-drop-title">{{ dragOver ? 'Drop to upload' : 'Drag & drop your logo' }}</span>
                <span class="cc-drop-sub">or click to browse</span>
              </div>
              <span v-if="logoPreviewUrl" class="cc-drop-overlay">
                <i class="bx bx-refresh"></i> Replace
              </span>
            </div>

            <input
              ref="logoFileInput"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              class="cc-file-hidden"
              @change="onLogoFileSelect"
            />

            <div class="cc-tabs" role="tablist" aria-label="Logo source">
              <button
                type="button"
                role="tab"
                class="cc-tab"
                :class="{ 'cc-tab--active': logoSource === 'upload' }"
                :aria-selected="logoSource === 'upload'"
                @click="logoSource = 'upload'"
              >
                <i class="bx bx-upload"></i> Upload file
              </button>
              <button
                type="button"
                role="tab"
                class="cc-tab"
                :class="{ 'cc-tab--active': logoSource === 'url' }"
                :aria-selected="logoSource === 'url'"
                @click="logoSource = 'url'"
              >
                <i class="bx bx-link"></i> Image URL
              </button>
            </div>

            <div v-if="logoSource === 'upload'" class="cc-tab-panel">
              <button type="button" class="cc-btn cc-btn--soft cc-btn--block" @click="openFilePicker">
                <i class="bx bx-folder-open"></i>
                {{ logoFile ? 'Choose another file' : 'Browse files' }}
              </button>
              <div v-if="logoFile" class="cc-file-chip">
                <i class="bx bx-image"></i>
                <span class="cc-file-name" :title="logoFile.name">{{ logoFile.name }}</span>
                <span class="cc-file-size">{{ formatBytes(logoFile.size) }}</span>
                <button type="button" class="cc-icon-btn" aria-label="Remove selected file" @click="clearLogoFile">
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <p v-else class="cc-help">The uploaded image replaces the current logo when you save.</p>
            </div>

            <div v-else class="cc-tab-panel">
              <div class="cc-input-wrap">
                <i class="bx bx-link-alt cc-input-icon"></i>
                <input
                  id="cc-logo-url"
                  v-model="form.logo"
                  type="text"
                  class="cc-input cc-input--icon"
                  :disabled="!!logoFile"
                  placeholder="https://example.com/logo.png"
                  aria-label="Logo image URL"
                  @input="onLogoUrlInput"
                />
              </div>
              <p v-if="logoFile" class="cc-help">
                <i class="bx bx-info-circle"></i>
                Remove the selected file to edit the URL.
              </p>
              <p v-else-if="logoPreviewError" class="cc-error">
                <i class="bx bx-error-circle"></i> That image could not be loaded. Check the URL.
              </p>
            </div>
          </div>

          <div class="cc-brand-col cc-brand-col--preview">
            <div class="cc-col-head">
              <h2 class="cc-col-title"><i class="bx bx-show"></i> Live preview</h2>
              <span class="cc-col-note">Payslip &amp; report header</span>
            </div>

            <div class="cc-doc">
              <div class="cc-doc-head">
                <div class="cc-doc-logo">
                  <img
                    v-if="logoPreviewUrl"
                    :src="logoPreviewUrl"
                    alt="Logo preview"
                    class="cc-doc-logo-img"
                    @error="onLogoError"
                  />
                  <span v-else class="cc-doc-logo-empty">LOGO</span>
                </div>
                <div class="cc-doc-identity">
                  <p class="cc-doc-name" :class="{ 'cc-doc-placeholder': !form.companyName }">
                    {{ form.companyName || 'Your company name' }}
                  </p>
                  <p v-if="form.tagline" class="cc-doc-tagline">{{ form.tagline }}</p>
                  <p v-if="form.companyAddress" class="cc-doc-address">{{ form.companyAddress }}</p>
                </div>
              </div>
              <div class="cc-doc-rule"></div>
              <div class="cc-doc-meta">
                <span v-if="form.companyEmail"><i class="bx bx-envelope"></i>{{ form.companyEmail }}</span>
                <span v-if="form.companyContact"><i class="bx bx-phone"></i>{{ form.companyContact }}</span>
                <span v-if="form.website">
                  <i class="bx bx-globe"></i>
                  <a :href="websiteHref" target="_blank" rel="noopener">{{ displayWebsite }}</a>
                </span>
                <span v-if="!form.companyEmail && !form.companyContact && !form.website" class="cc-doc-placeholder">
                  Contact details appear here
                </span>
              </div>
              <p class="cc-doc-caption">PAYSLIP · {{ currentPeriod }}</p>
              <div class="cc-doc-lines">
                <span></span><span></span><span class="cc-doc-line--short"></span>
              </div>
            </div>

            <div class="cc-meter">
              <div class="cc-meter-top">
                <span class="cc-meter-label">Profile completeness</span>
                <span class="cc-meter-value">{{ completeness.percent }}%</span>
              </div>
              <div
                class="cc-meter-bar"
                role="progressbar"
                :aria-valuenow="completeness.percent"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div class="cc-meter-fill" :style="{ width: completeness.percent + '%' }"></div>
              </div>
              <div v-if="completeness.missing.length" class="cc-meter-missing">
                <span class="cc-meter-missing-label">Still missing</span>
                <button
                  v-for="item in completeness.missing"
                  :key="item.key"
                  type="button"
                  class="cc-chip"
                  @click="focusField(item.key)"
                >
                  <i class="bx bx-plus"></i> {{ item.label }}
                </button>
              </div>
              <p v-else class="cc-help cc-help--ok">
                <i class="bx bx-check-circle"></i> Every detail is filled in.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Details -->
      <section class="cc-card">
        <header class="cc-card-header">
          <span class="cc-card-icon"><i class="bx bx-building-house"></i></span>
          <div class="cc-card-head-text">
            <h2 class="cc-card-title">Organisation details</h2>
            <p class="cc-card-desc">Shown on payslips, HR letters and system emails</p>
          </div>
        </header>

        <div class="cc-card-body">
          <div class="cc-group">
            <p class="cc-group-title">Identity</p>
            <div class="cc-grid">
              <div class="cc-field cc-field--full">
                <label for="cc-company-name" class="cc-label">
                  Company / organisation name <span class="cc-req">*</span>
                </label>
                <div class="cc-input-wrap">
                  <i class="bx bx-building cc-input-icon"></i>
                  <input
                    id="cc-company-name"
                    ref="companyName"
                    v-model="form.companyName"
                    type="text"
                    class="cc-input cc-input--icon"
                    :class="{ 'cc-input--invalid': showError('companyName') }"
                    :aria-invalid="showError('companyName') ? 'true' : 'false'"
                    aria-describedby="cc-company-name-error"
                    placeholder="e.g. Craft Silicon Payments Limited"
                    @blur="touch('companyName')"
                  />
                </div>
                <p v-if="showError('companyName')" id="cc-company-name-error" class="cc-error">
                  <i class="bx bx-error-circle"></i> {{ errors.companyName }}
                </p>
              </div>

              <div class="cc-field cc-field--full">
                <label for="cc-tagline" class="cc-label">Tagline</label>
                <div class="cc-input-wrap">
                  <i class="bx bx-quote-left cc-input-icon"></i>
                  <input
                    id="cc-tagline"
                    ref="tagline"
                    v-model="form.tagline"
                    type="text"
                    class="cc-input cc-input--icon"
                    maxlength="120"
                    placeholder="A short line printed under the company name"
                  />
                </div>
                <p class="cc-help cc-help--right">{{ (form.tagline || '').length }}/120</p>
              </div>
            </div>
          </div>

          <div class="cc-group">
            <p class="cc-group-title">Contact</p>
            <div class="cc-grid">
              <div class="cc-field">
                <label for="cc-company-email" class="cc-label">Email</label>
                <div class="cc-input-wrap">
                  <i class="bx bx-envelope cc-input-icon"></i>
                  <input
                    id="cc-company-email"
                    ref="companyEmail"
                    v-model="form.companyEmail"
                    type="email"
                    class="cc-input cc-input--icon"
                    :class="{ 'cc-input--invalid': showError('companyEmail') }"
                    :aria-invalid="showError('companyEmail') ? 'true' : 'false'"
                    aria-describedby="cc-company-email-error"
                    placeholder="info@company.com"
                    @blur="touch('companyEmail')"
                  />
                </div>
                <p v-if="showError('companyEmail')" id="cc-company-email-error" class="cc-error">
                  <i class="bx bx-error-circle"></i> {{ errors.companyEmail }}
                </p>
              </div>

              <div class="cc-field">
                <label for="cc-company-contact" class="cc-label">Phone / contact</label>
                <div class="cc-input-wrap">
                  <i class="bx bx-phone cc-input-icon"></i>
                  <input
                    id="cc-company-contact"
                    ref="companyContact"
                    v-model="form.companyContact"
                    type="text"
                    class="cc-input cc-input--icon"
                    placeholder="+254 700 000 000"
                  />
                </div>
              </div>

              <div class="cc-field">
                <label for="cc-website" class="cc-label">Website</label>
                <div class="cc-input-wrap">
                  <i class="bx bx-globe cc-input-icon"></i>
                  <input
                    id="cc-website"
                    ref="website"
                    v-model="form.website"
                    type="text"
                    inputmode="url"
                    class="cc-input cc-input--icon"
                    :class="{ 'cc-input--invalid': showError('website') }"
                    :aria-invalid="showError('website') ? 'true' : 'false'"
                    aria-describedby="cc-website-error"
                    placeholder="company.example.com"
                    @blur="onWebsiteBlur"
                  />
                </div>
                <p v-if="showError('website')" id="cc-website-error" class="cc-error">
                  <i class="bx bx-error-circle"></i> {{ errors.website }}
                </p>
              </div>

              <div class="cc-field">
                <label for="cc-address" class="cc-label">Address</label>
                <textarea
                  id="cc-address"
                  ref="companyAddress"
                  v-model="form.companyAddress"
                  class="cc-input cc-textarea"
                  rows="2"
                  placeholder="Physical address or P.O. Box"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Sticky action bar -->
      <transition name="cc-bar">
        <div v-if="isDirty" class="cc-actionbar">
          <span class="cc-actionbar-text">
            <i class="bx bx-info-circle"></i>
            You have unsaved changes
          </span>
          <div class="cc-actionbar-buttons">
            <button type="button" class="cc-btn cc-btn--ghost" :disabled="saving" @click="discard">
              <i class="bx bx-undo"></i> Discard
            </button>
            <button type="submit" class="cc-btn cc-btn--primary" :disabled="saving">
              <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
              {{ saving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </div>
      </transition>
    </form>
  </HrPageLayout>
</template>

<script>
import { authUrl, base, execute, toast } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

const ALLOWED_LOGO_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const emptyForm = () => ({
  companyName: "",
  companyAddress: "",
  companyContact: "",
  companyEmail: "",
  website: "",
  tagline: "",
  logo: ""
});

export default {
  name: "CompanyConfiguration",
  components: { HrPageLayout },
  data() {
    return {
      loading: true,
      loadError: false,
      saving: false,
      dragOver: false,
      logoSource: "upload",
      logoFile: null,
      logoObjectUrl: null,
      logoPreviewError: false,
      submitAttempted: false,
      touched: {},
      form: emptyForm(),
      savedForm: emptyForm()
    };
  },
  computed: {
    logoPreviewUrl() {
      if (this.logoObjectUrl) return this.logoObjectUrl;
      if (this.logoPreviewError) return null;
      const logo = (this.form.logo || "").trim();
      if (!logo) return null;
      if (logo.startsWith("http://") || logo.startsWith("https://") || logo.startsWith("data:")) {
        return logo;
      }
      return base + "/" + logo.replace(/^\//, "");
    },
    websiteHref() {
      const site = (this.form.website || "").trim();
      if (!site) return "#";
      return /^https?:\/\//i.test(site) ? site : "https://" + site;
    },
    displayWebsite() {
      return (this.form.website || "").trim().replace(/^https?:\/\//i, "");
    },
    currentPeriod() {
      return new Date()
        .toLocaleDateString(undefined, { month: "long", year: "numeric" })
        .toUpperCase();
    },
    errors() {
      const errors = {};
      if (!(this.form.companyName || "").trim()) {
        errors.companyName = "Company name is required.";
      }
      const email = (this.form.companyEmail || "").trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        errors.companyEmail = "Enter a valid email address.";
      }
      const site = (this.form.website || "").trim();
      if (site && !/^([a-z][a-z0-9+.-]*:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(site)) {
        errors.website = "Enter a valid website address.";
      }
      return errors;
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    isDirty() {
      if (this.logoFile) return true;
      return Object.keys(this.savedForm).some(
        (key) => (this.form[key] || "") !== (this.savedForm[key] || "")
      );
    },
    completeness() {
      const fields = [
        { key: "companyName", label: "Company name" },
        { key: "companyEmail", label: "Email" },
        { key: "companyContact", label: "Phone" },
        { key: "companyAddress", label: "Address" },
        { key: "website", label: "Website" },
        { key: "tagline", label: "Tagline" },
        { key: "logo", label: "Logo" }
      ];
      const missing = [];
      let filled = 0;
      fields.forEach((field) => {
        const hasValue =
          field.key === "logo"
            ? Boolean(this.logoPreviewUrl)
            : Boolean((this.form[field.key] || "").trim());
        if (hasValue) filled += 1;
        else missing.push(field);
      });
      return {
        percent: Math.round((filled / fields.length) * 100),
        missing
      };
    }
  },
  mounted() {
    this.load();
    window.addEventListener("beforeunload", this.warnOnUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.warnOnUnload);
    this.revokeObjectUrl();
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !window.confirm("You have unsaved changes. Leave without saving?")) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    warnOnUnload(event) {
      if (!this.isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    },
    revokeObjectUrl() {
      if (!this.logoObjectUrl) return;
      try {
        URL.revokeObjectURL(this.logoObjectUrl);
      } catch {
        // Revoke can fail if the URL was already revoked; ignore
      }
      this.logoObjectUrl = null;
    },
    touch(field) {
      this.touched = { ...this.touched, [field]: true };
    },
    showError(field) {
      return Boolean(this.errors[field] && (this.touched[field] || this.submitAttempted));
    },
    formatBytes(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    focusField(key) {
      if (key === "logo") {
        this.openFilePicker();
        return;
      }
      const el = this.$refs[key];
      if (el && typeof el.focus === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
      }
    },
    load() {
      this.loading = true;
      this.loadError = false;
      const data = new FormData();
      data.append("function", "getCompanyInfo");

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          const company = res.data?.data?.company;
          if (res.data?.success && company) {
            this.form = {
              companyName: company.companyName || company.countyName || "",
              companyAddress: company.companyAddress || "",
              companyContact: company.companyContact || "",
              companyEmail: company.companyEmail || "",
              website: company.website || "",
              tagline: company.tagline || "",
              logo: company.logo || ""
            };
            this.savedForm = { ...this.form };
            this.logoPreviewError = false;
            this.touched = {};
            this.submitAttempted = false;
          } else {
            this.loadError = true;
          }
        })
        .catch(() => {
          this.loading = false;
          this.loadError = true;
        });
    },
    discard() {
      if (!this.isDirty) return;
      if (!window.confirm("Discard all unsaved changes?")) return;
      this.clearLogoFile();
      this.form = { ...this.savedForm };
      this.touched = {};
      this.submitAttempted = false;
      this.logoPreviewError = false;
      toast.info("Changes discarded.");
    },
    openFilePicker() {
      this.logoSource = "upload";
      this.$refs.logoFileInput?.click();
    },
    onLogoFileSelect(e) {
      const file = e.target.files && e.target.files[0];
      this.applyLogoFile(file);
    },
    onLogoDrop(e) {
      this.dragOver = false;
      this.applyLogoFile(e.dataTransfer?.files?.[0]);
    },
    applyLogoFile(file) {
      if (!file) return;
      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        toast.error("Unsupported file type. Use a PNG, JPG, GIF or WebP image.");
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        toast.error("That image is " + this.formatBytes(file.size) + ". Use an image under 2MB.");
        return;
      }
      this.revokeObjectUrl();
      this.logoFile = file;
      this.logoObjectUrl = URL.createObjectURL(file);
      this.logoPreviewError = false;
      this.logoSource = "upload";
    },
    clearLogoFile() {
      this.revokeObjectUrl();
      this.logoFile = null;
      if (this.$refs.logoFileInput) this.$refs.logoFileInput.value = "";
    },
    onLogoUrlInput() {
      this.logoPreviewError = false;
    },
    onLogoError() {
      this.logoPreviewError = true;
    },
    onWebsiteBlur() {
      const site = (this.form.website || "").trim();
      if (site && !/^https?:\/\//i.test(site) && !this.errors.website) {
        this.form.website = "https://" + site;
      }
      this.touch("website");
    },
    save() {
      this.submitAttempted = true;
      if (this.hasErrors) {
        const firstKey = Object.keys(this.errors)[0];
        toast.error(this.errors[firstKey]);
        this.focusField(firstKey);
        return;
      }
      if (!this.isDirty) return;

      this.saving = true;
      const data = new FormData();
      data.append("function", "updateCompanyInfo");
      data.append("companyName", (this.form.companyName || "").trim());
      data.append("companyAddress", (this.form.companyAddress || "").trim());
      data.append("companyContact", (this.form.companyContact || "").trim());
      data.append("companyEmail", (this.form.companyEmail || "").trim());
      data.append("website", (this.form.website || "").trim());
      data.append("tagline", (this.form.tagline || "").trim());
      data.append("logo", (this.form.logo || "").trim());

      if (this.logoFile) {
        data.append("logoFile", this.logoFile);
      }

      execute(data, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data?.success) {
            if (res.data.data?.logo) {
              this.form.logo = res.data.data.logo;
            }
            this.clearLogoFile();
            this.savedForm = { ...this.form };
            this.touched = {};
            this.submitAttempted = false;
            toast.success("Company details updated successfully.");
          } else {
            toast.error(res.data?.message || "Failed to update company details.");
          }
        })
        .catch(() => {
          this.saving = false;
          toast.error("Error saving company configuration.");
        });
    }
  }
};
</script>

<style scoped>
.cc-wrap {
  padding-bottom: 90px;
}

/* Cards */
.cc-card {
  background: #fff;
  border: 1px solid #eaeef5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 12px 28px -20px rgba(16, 24, 40, 0.25);
  margin-bottom: 1.25rem;
  overflow: hidden;
}
.cc-card:last-of-type {
  margin-bottom: 0;
}
.cc-card-header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.1rem 1.5rem;
  background: linear-gradient(180deg, #fbfcfe 0%, #fff 100%);
  border-bottom: 1px solid #f1f4f9;
}
.cc-card-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  border-radius: 12px;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 6px 14px -6px rgba(79, 70, 229, 0.7);
}
.cc-card-head-text {
  min-width: 0;
}
.cc-card-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: #101828;
  margin: 0;
}
.cc-card-desc {
  font-size: 0.8rem;
  color: #667085;
  margin: 0.1rem 0 0;
}
.cc-card-body {
  padding: 1.5rem;
}

/* Brand block */
.cc-brand-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
}
.cc-brand-col {
  padding: 1.5rem;
  min-width: 0;
}
.cc-brand-col--preview {
  background: linear-gradient(180deg, #f8faff 0%, #f4f6fc 100%);
  border-left: 1px solid #eef1f7;
}
@media (max-width: 1024px) {
  .cc-brand-grid {
    grid-template-columns: 1fr;
  }
  .cc-brand-col--preview {
    border-left: none;
    border-top: 1px solid #eef1f7;
  }
}
.cc-col-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}
.cc-col-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #101828;
  margin: 0;
}
.cc-col-title i {
  color: #6366f1;
  font-size: 1.1rem;
}
.cc-col-note {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #98a2b3;
}

/* Drop zone */
.cc-drop {
  position: relative;
  min-height: 152px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border: 1.5px dashed #d0d7e5;
  border-radius: 14px;
  background: #fafbfe;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
}
.cc-drop:hover,
.cc-drop:focus-visible {
  border-color: #6366f1;
  background: #f5f4ff;
  outline: none;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
}
.cc-drop--active {
  border-color: #4f46e5;
  border-style: solid;
  background: #eef2ff;
}
.cc-drop--filled {
  background: #fff;
  border-style: solid;
  border-color: #e4e8f0;
}
.cc-drop-img {
  max-width: 100%;
  max-height: 128px;
  object-fit: contain;
}
.cc-drop-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.15rem;
}
.cc-drop-empty i {
  font-size: 1.9rem;
  color: #a5b4fc;
  margin-bottom: 0.25rem;
}
.cc-drop-title {
  font-size: 0.88rem;
  font-weight: 600;
  color: #344054;
}
.cc-drop-sub {
  font-size: 0.76rem;
  color: #98a2b3;
}
.cc-drop-overlay {
  position: absolute;
  inset: auto 0.5rem 0.5rem auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475467;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e4e8f0;
  border-radius: 999px;
  opacity: 0;
  transition: opacity 0.18s;
}
.cc-drop:hover .cc-drop-overlay,
.cc-drop:focus-visible .cc-drop-overlay {
  opacity: 1;
}
.cc-file-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

/* Tabs */
.cc-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  margin: 1rem 0 0.85rem;
  padding: 0.25rem;
  background: #f2f4f8;
  border-radius: 10px;
}
.cc-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.45rem 0.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #667085;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}
.cc-tab:hover {
  color: #344054;
}
.cc-tab--active {
  color: #4f46e5;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
}
.cc-tab-panel {
  min-height: 84px;
}

/* Live document preview */
.cc-doc {
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 12px;
  padding: 1.15rem 1.25rem 1.35rem;
  box-shadow: 0 10px 24px -18px rgba(16, 24, 40, 0.45);
}
.cc-doc-head {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.cc-doc-logo {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eaeef5;
  overflow: hidden;
}
.cc-doc-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.cc-doc-logo-empty {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #cbd5e1;
}
.cc-doc-identity {
  min-width: 0;
}
.cc-doc-name {
  font-size: 1.1rem;
  font-weight: 800;
  color: #101828;
  margin: 0;
  line-height: 1.25;
  word-break: break-word;
}
.cc-doc-tagline {
  font-size: 0.82rem;
  color: #475467;
  margin: 0.2rem 0 0;
}
.cc-doc-address {
  font-size: 0.76rem;
  color: #667085;
  margin: 0.3rem 0 0;
  white-space: pre-line;
}
.cc-doc-placeholder {
  color: #b6bfcd;
  font-weight: 600;
}
.cc-doc-rule {
  height: 3px;
  margin: 0.9rem 0 0.75rem;
  border-radius: 3px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 55%, #e9d5ff 100%);
}
.cc-doc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.1rem;
  font-size: 0.76rem;
  color: #475467;
}
.cc-doc-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  min-width: 0;
  word-break: break-all;
}
.cc-doc-meta i {
  color: #6366f1;
  font-size: 0.95rem;
}
.cc-doc-meta a {
  color: #4f46e5;
  text-decoration: none;
}
.cc-doc-meta a:hover {
  text-decoration: underline;
}
.cc-doc-caption {
  margin: 1.15rem 0 0.55rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #b6bfcd;
}
.cc-doc-lines {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.cc-doc-lines span {
  height: 7px;
  border-radius: 4px;
  background: #f1f4f9;
}
.cc-doc-line--short {
  width: 48%;
}

/* Completeness meter */
.cc-meter {
  margin-top: 1.15rem;
}
.cc-meter-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}
.cc-meter-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344054;
}
.cc-meter-value {
  font-size: 0.8rem;
  font-weight: 800;
  color: #4f46e5;
}
.cc-meter-bar {
  height: 8px;
  background: #e7ebf3;
  border-radius: 999px;
  overflow: hidden;
}
.cc-meter-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
  transition: width 0.35s ease;
}
.cc-meter-missing {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.7rem;
}
.cc-meter-missing-label {
  font-size: 0.75rem;
  color: #667085;
  margin-right: 0.15rem;
}
.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  font-size: 0.74rem;
  font-weight: 600;
  color: #475467;
  background: #fff;
  border: 1px solid #e0e5ee;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.cc-chip:hover {
  color: #4f46e5;
  border-color: #c7d2fe;
  background: #f5f4ff;
}
.cc-chip i {
  font-size: 0.85rem;
}

/* Field groups */
.cc-group + .cc-group {
  margin-top: 1.6rem;
  padding-top: 1.4rem;
  border-top: 1px dashed #e9edf5;
}
.cc-group-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #98a2b3;
  margin: 0 0 0.9rem;
}
.cc-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.1rem 1.25rem;
}
.cc-field--full {
  grid-column: 1 / -1;
}
@media (max-width: 700px) {
  .cc-grid {
    grid-template-columns: 1fr;
  }
}
.cc-field {
  min-width: 0;
}
.cc-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #344054;
  margin-bottom: 0.35rem;
}
.cc-req {
  color: #d92d20;
}
.cc-input-wrap {
  position: relative;
}
.cc-input-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  font-size: 1.05rem;
  color: #98a2b3;
  pointer-events: none;
  transition: color 0.15s;
}
.cc-input-wrap:focus-within .cc-input-icon {
  color: #6366f1;
}
.cc-input {
  width: 100%;
  padding: 0.62rem 0.85rem;
  font-size: 0.92rem;
  color: #101828;
  background: #fff;
  border: 1px solid #dfe4ed;
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.cc-input--icon {
  padding-left: 2.35rem;
}
.cc-input:hover:not(:disabled) {
  border-color: #cdd5e3;
}
.cc-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
}
.cc-input:disabled {
  background: #f7f8fb;
  color: #98a2b3;
  cursor: not-allowed;
}
.cc-input::placeholder {
  color: #a9b2c1;
}
.cc-input--invalid {
  border-color: #f97066;
}
.cc-input--invalid:focus {
  border-color: #d92d20;
  box-shadow: 0 0 0 4px rgba(217, 45, 32, 0.12);
}
.cc-textarea {
  resize: vertical;
  min-height: 74px;
  line-height: 1.5;
}
.cc-error {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.76rem;
  color: #d92d20;
  margin: 0.35rem 0 0;
}
.cc-help {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.76rem;
  color: #667085;
  margin: 0.4rem 0 0;
}
.cc-help--right {
  justify-content: flex-end;
}
.cc-help--ok {
  color: #039855;
  font-weight: 600;
}

/* File chip */
.cc-file-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.6rem;
  padding: 0.5rem 0.55rem 0.5rem 0.7rem;
  background: #f7f8fb;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  font-size: 0.8rem;
  color: #344054;
}
.cc-file-chip > i {
  color: #6366f1;
  font-size: 1.05rem;
}
.cc-file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}
.cc-file-size {
  color: #667085;
  white-space: nowrap;
}
.cc-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: #667085;
  background: #fff;
  border: 1px solid #e0e5ee;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.cc-icon-btn:hover {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

/* Buttons */
.cc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.55rem 1.05rem;
  font-size: 0.88rem;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, transform 0.1s, opacity 0.15s;
}
.cc-btn--primary {
  color: #fff;
  border: none;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  box-shadow: 0 8px 18px -10px rgba(79, 70, 229, 0.9);
}
.cc-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
}
.cc-btn--ghost {
  color: #475467;
  background: #fff;
  border: 1px solid #dfe4ed;
}
.cc-btn--ghost:hover:not(:disabled) {
  background: #f7f8fb;
  border-color: #cdd5e3;
}
.cc-btn--soft {
  color: #4f46e5;
  background: #f0f0ff;
  border: 1px solid #dcdcff;
}
.cc-btn--soft:hover:not(:disabled) {
  background: #e6e6ff;
}
.cc-btn--block {
  width: 100%;
}
.cc-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Status pills */
.cc-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 600;
  border-radius: 999px;
}
.cc-pill--warn {
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
}
.cc-pill--ok {
  color: #027a48;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

/* Sticky action bar */
.cc-actionbar {
  position: fixed;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 900;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.7rem 0.8rem 0.7rem 1.15rem;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid #e4e8f0;
  border-radius: 14px;
  box-shadow: 0 18px 40px -18px rgba(16, 24, 40, 0.45);
  backdrop-filter: blur(6px);
}
.cc-actionbar-text {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475467;
}
.cc-actionbar-text i {
  color: #f79009;
  font-size: 1.05rem;
}
.cc-actionbar-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
@media (max-width: 640px) {
  .cc-actionbar {
    left: 12px;
    right: 12px;
    transform: none;
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
  }
  .cc-actionbar-buttons .cc-btn {
    flex: 1;
  }
}
.cc-bar-enter-active,
.cc-bar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.cc-bar-enter-from,
.cc-bar-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

/* Empty / error state */
.cc-empty {
  text-align: center;
  padding: 3.25rem 1.5rem;
}
.cc-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f2f4f8;
  color: #98a2b3;
  font-size: 1.7rem;
}
.cc-empty-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #101828;
  margin: 0.9rem 0 0.3rem;
}
.cc-empty-text {
  font-size: 0.88rem;
  color: #667085;
  margin: 0 0 1.25rem;
}

/* Skeletons */
.cc-skeleton-card {
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
}
.cc-skeleton-card--form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.1rem 1.25rem;
}
.cc-sk {
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 37%, #eef1f6 63%);
  background-size: 400% 100%;
  animation: cc-shimmer 1.3s ease-in-out infinite;
  border-radius: 10px;
}
@keyframes cc-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
.cc-sk-logo {
  width: 180px;
  height: 150px;
  flex-shrink: 0;
}
.cc-sk-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.5rem;
}
.cc-sk-line {
  height: 14px;
}
.cc-sk-line--lg {
  height: 24px;
  width: 55%;
}
.cc-sk-line--sm {
  width: 35%;
}
.cc-sk-field {
  height: 62px;
}
@media (max-width: 700px) {
  .cc-skeleton-card,
  .cc-skeleton-card--form {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
  .cc-sk-logo {
    width: 100%;
  }
}
</style>
